import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import type { WidgetSize } from "@/lib/api/dashboardLayout";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorState } from "@/components/ui/EmptyState";
import { cn } from "@/lib/utils/cn";
import { KpiCard } from "../../sections";
import type { KPI_TONE_CLASSES } from "../../sections-utils";
import type { DataState } from "./state";

/** What every widget component receives: the size preset it is shown at. */
export interface WidgetProps {
  size: WidgetSize;
}

const FILL = "h-full [&>a]:h-full [&>a>div]:h-full [&>div]:h-full";

/**
 * A titled card that fills its grid cell and owns the three states every
 * widget must handle: a skeleton the height of the content while loading
 * (so nothing jumps when data arrives), a non-technical error with Retry
 * (so one failed request never breaks the dashboard), and the content.
 * `children` are only rendered with real data - never a fabricated zero.
 */
export function WidgetCard({
  title,
  action,
  state,
  children,
  bodyClassName,
  minBodyHeight = "min-h-[8rem]",
}: {
  title: string;
  action?: ReactNode;
  state: DataState;
  children: ReactNode;
  bodyClassName?: string;
  minBodyHeight?: string;
}) {
  return (
    <Card interactive className="flex h-full min-w-0 flex-col">
      <CardHeader>
        <CardTitle className="truncate">{title}</CardTitle>
        {action}
      </CardHeader>
      <CardBody className={cn("min-w-0 flex-1", minBodyHeight, bodyClassName)}>
        {state.isLoading ? (
          <div className="space-y-3" aria-busy="true" aria-label={`Loading ${title}`}>
            <Skeleton className="h-5 w-1/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : state.isError ? (
          <ErrorState title={`Couldn't load ${title.toLowerCase()}`} description="Please try again." onRetry={() => state.refetch()} className="py-4" />
        ) : (
          children
        )}
      </CardBody>
    </Card>
  );
}

export function ViewAllLink({ to, label = "View all →" }: { to: string; label?: string }) {
  return (
    <Link to={to} className="shrink-0 text-xs font-medium text-primary-700 hover:underline">
      {label}
    </Link>
  );
}

/**
 * A KPI card widget. While loading it shows a card-shaped skeleton; on a
 * failed request it shows a retryable error instead of "0"; with data it is
 * the very same KpiCard the original dashboard uses.
 */
export function KpiWidget({
  label,
  icon,
  tone,
  to,
  state,
  value,
  hint,
  trend,
  sparkline,
}: {
  label: string;
  icon: string;
  tone: keyof typeof KPI_TONE_CLASSES;
  to?: string;
  state: DataState;
  /** undefined once loaded means "no data": shown as unavailable, not zero. */
  value: ReactNode | undefined;
  hint?: string;
  trend?: { value: string; positive: boolean };
  sparkline?: number[];
}) {
  if (state.isLoading) {
    return (
      <Card className="h-full" aria-busy="true" aria-label={`Loading ${label}`}>
        <CardBody>
          <Skeleton className="h-10 w-10 rounded-xl2" />
          <Skeleton className="mt-3 h-7 w-20" />
          <Skeleton className="mt-2 h-3 w-28" />
        </CardBody>
      </Card>
    );
  }
  if (state.isError || value === undefined) {
    return (
      <Card className="h-full">
        <CardBody className="flex h-full flex-col justify-between gap-2">
          <p className="text-xs font-medium text-slate-500">{label}</p>
          <p className="text-sm text-slate-500">Couldn&apos;t load this figure.</p>
          <button type="button" onClick={() => state.refetch()} className="self-start text-xs font-medium text-primary-700 underline hover:no-underline">
            Retry
          </button>
        </CardBody>
      </Card>
    );
  }
  return (
    <div className={FILL}>
      <KpiCard label={label} value={value} hint={hint} icon={icon} tone={tone} to={to} trend={trend} sparkline={sparkline} />
    </div>
  );
}

export interface SummaryRow {
  label: string;
  value: ReactNode;
  /** Colours the figure to flag something that needs attention. */
  tone?: "default" | "warn" | "good";
  to?: string;
}

const ROW_TONE = { default: "text-slate-900", warn: "text-amber-600", good: "text-emerald-600" } as const;

/** A label / figure list - the shape most "summary" widgets share. */
export function SummaryRows({ rows }: { rows: SummaryRow[] }) {
  return (
    <ul className="divide-y divide-slate-100">
      {rows.map((row) => {
        const inner = (
          <>
            <span className="min-w-0 truncate text-sm text-slate-500">{row.label}</span>
            <span className={cn("shrink-0 text-sm font-semibold tabular-nums", ROW_TONE[row.tone ?? "default"])}>{row.value}</span>
          </>
        );
        return (
          <li key={row.label}>
            {row.to ? (
              <Link to={row.to} className="flex items-center justify-between gap-3 rounded-md px-1 py-2 transition-colors hover:bg-primary-50/40">
                {inner}
              </Link>
            ) : (
              <div className="flex items-center justify-between gap-3 px-1 py-2">{inner}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** A card of SummaryRows backed by one query. */
export function SummaryWidget({
  title,
  state,
  rows,
  link,
  note,
}: {
  title: string;
  state: DataState;
  /** undefined (no data) renders the empty message instead of zero rows. */
  rows: SummaryRow[] | undefined;
  link?: { to: string; label?: string };
  note?: string;
}) {
  return (
    <WidgetCard title={title} state={state} action={link ? <ViewAllLink to={link.to} label={link.label} /> : undefined}>
      {rows && rows.length > 0 ? (
        <>
          <SummaryRows rows={rows} />
          {note && <p className="mt-2 text-xs text-slate-400">{note}</p>}
        </>
      ) : (
        <p className="py-6 text-center text-sm text-slate-400">No data yet</p>
      )}
    </WidgetCard>
  );
}
